import React from 'react';
import './PrintInvoice.css';

const PrintInvoice = ({ invoice, onClose }) => {
  if (!invoice) return null;

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-GB');
  };

  const formatCurrency = (amount) => {
    return `KES ${Number(amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;
  };

  const items = invoice.items || invoice.products || invoice.line_items || [];
  
  const getItemDescription = (item) => {
    return item.description || item.itemName || item.item_name || 'Service';
  };
  
  const getItemQty = (item) => {
    return item.quantity || item.qty || 1;
  };
  
  const getItemRate = (item) => {
    return item.rate || item.unit_price || item.unitPrice || 0;
  };
  
  const getItemAmount = (item) => {
    return item.amount || item.line_total || (getItemQty(item) * getItemRate(item)) || 0;
  };

  return (
    <div className="print-overlay">
      <div className="print-modal">
        <div className="print-actions no-print">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          <button className="btn btn-primary" onClick={() => window.print()}>🖨️ Print Now</button>
        </div>

        <div className="invoice-print-content">
          <div className="invoice-header-modern">
            <div className="invoice-header-left">
              <div className="invoice-logo-area">
                <img 
                  src="/logos/gismac.jpg" 
                  alt="GISMAC Security" 
                  className="invoice-real-logo"
                  style={{ width: '100px', height: '100px', objectFit: 'contain', borderRadius: '50%' }}
                />
              </div>
              <div className="invoice-company-details">
                <h2>Gismac Security Limited</h2>
                <p className="company-tagline">Professional Security Solutions</p>
                <div className="company-contact">
                  <p>📧 accounts@gismac.co.ke</p>
                  <p>🌐 www.secs.gismac.co.ke</p>
                  <p>📍 Nairobi, Kenya</p>
                </div>
                {invoice.companyPin && <p className="company-pin"><strong>PIN:</strong> {invoice.companyPin}</p>}
              </div>
            </div>
            <div className="invoice-header-right">
              <div className="invoice-doc-badge">
                <span className="doc-type">INVOICE</span>
                <span className="doc-number">#{invoice.invoiceNumber || invoice.invoice_number || invoice.invoiceNo || 'N/A'}</span>
              </div>
              <div className="invoice-meta">
                <div className="meta-row"><span className="meta-label">Issue Date:</span><span className="meta-value">{formatDate(invoice.issueDate || invoice.issue_date || invoice.createdAt)}</span></div>
                <div className="meta-row"><span className="meta-label">Due Date:</span><span className="meta-value">{formatDate(invoice.dueDate || invoice.due_date)}</span></div>
                <div className="meta-row"><span className="meta-label">Status:</span><span className={`meta-status ${(invoice.status || 'draft').toLowerCase()}`}>{(invoice.status || 'Draft').toUpperCase()}</span></div>
              </div>
            </div>
          </div>

          <div className="invoice-details-modern">
            <div className="detail-card client-card">
              <div className="card-header"><span className="card-icon">👤</span><h4>Bill To</h4></div>
              <div className="card-body">
                <p className="client-name">{invoice.clientName || invoice.client_name || invoice.client || 'N/A'}</p>
                {invoice.customerPin && <p className="client-pin"><strong>Customer PIN:</strong> {invoice.customerPin}</p>}
                {invoice.clientEmail && <p className="client-contact">📧 {invoice.clientEmail}</p>}
                {invoice.clientPhone && <p className="client-contact">📞 {invoice.clientPhone}</p>}
                {invoice.clientAddress && <p className="client-address">{invoice.clientAddress}</p>}
              </div>
            </div>
            <div className="detail-card info-card">
              <div className="card-header"><span className="card-icon">📋</span><h4>Invoice Details</h4></div>
              <div className="card-body">
                <div className="info-row"><span className="info-label">Payment Terms:</span><span className="info-value">Net 30 Days</span></div>
                {invoice.location && <div className="info-row"><span className="info-label">Location:</span><span className="info-value">{invoice.location}</span></div>}
                {invoice.issuedBy && <div className="info-row"><span className="info-label">Issued By:</span><span className="info-value">accounts@gismac.co.ke</span></div>}
                <div className="info-row"><span className="info-label">Currency:</span><span className="info-value">Kenyan Shilling (KES)</span></div>
              </div>
            </div>
          </div>

          <div className="invoice-table-section">
            <h3 className="section-title">Line Items</h3>
            <table className="invoice-table-modern">
              <thead>
                <tr>
                  <th className="col-num">#</th>
                  <th className="col-desc">Description</th>
                  <th className="col-qty">Qty</th>
                  <th className="col-rate">Rate (KES)</th>
                  <th className="col-amount">Amount (KES)</th>
                </tr>
              </thead>
              <tbody>
                {items.length > 0 ? items.map((item, index) => (
                  <tr key={index}>
                    <td className="col-num">{index + 1}</td>
                    <td className="col-desc">{getItemDescription(item)}</td>
                    <td className="col-qty">{getItemQty(item)}</td>
                    <td className="col-rate">{formatCurrency(getItemRate(item)).replace('KES ', '')}</td>
                    <td className="col-amount">{formatCurrency(getItemAmount(item)).replace('KES ', '')}</td>
                  </tr>
                )) : (
                  <tr>
                    <td className="col-num">1</td>
                    <td className="col-desc">{invoice.description || 'Security Services'}</td>
                    <td className="col-qty">1</td>
                    <td className="col-rate">{formatCurrency(invoice.amount || invoice.totalAmount || invoice.total_amount || 0).replace('KES ', '')}</td>
                    <td className="col-amount">{formatCurrency(invoice.amount || invoice.totalAmount || invoice.total_amount || 0).replace('KES ', '')}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="invoice-summary-modern">
            <div className="summary-box">
              <div className="summary-line"><span className="summary-label">Subtotal</span><span className="summary-value">{formatCurrency(invoice.subtotal || 0)}</span></div>
              <div className="summary-line"><span className="summary-label">Tax ({invoice.taxRate || invoice.tax_rate || 0}%)</span><span className="summary-value">{formatCurrency(invoice.taxAmount || invoice.tax_amount || 0)}</span></div>
              {invoice.discount > 0 && <div className="summary-line discount"><span className="summary-label">Discount</span><span className="summary-value">-{formatCurrency(invoice.discount)}</span></div>}
              <div className="summary-line total"><span className="summary-label">Total Amount</span><span className="summary-value">{formatCurrency(invoice.totalAmount || invoice.total_amount || invoice.amount || 0)}</span></div>
              <div className="summary-line paid"><span className="summary-label">Amount Paid</span><span className="summary-value">{formatCurrency(invoice.amountPaid || invoice.amount_paid || 0)}</span></div>
              <div className="summary-line balance"><span className="summary-label">Balance Due</span><span className="summary-value">{formatCurrency(invoice.balanceDue || invoice.balance_due || (invoice.totalAmount || invoice.amount || 0) - (invoice.amountPaid || 0))}</span></div>
            </div>
          </div>

          {invoice.notes && (
            <div className="invoice-notes-modern">
              <h4>📝 Notes</h4>
              <p>{invoice.notes}</p>
            </div>
          )}

          <div className="invoice-footer-modern">
            <div className="footer-left">
              <h4>Payment Information</h4>
              <p>Please make payment by the due date specified above.</p>
              <p><strong>Bank:</strong> Equity Bank Kenya</p>
              <p><strong>Account:</strong> Gismac Security Ltd</p>
              <p><strong>Swift:</strong> EQBLKENA</p>
            </div>
            <div className="footer-right">
              <div className="signature-block">
                <p className="sig-line">Authorized Signature</p>
                <div className="sig-underline"></div>
                <p className="sig-date">Date: _______________</p>
              </div>
              <div className="stamp-placeholder"><span>OFFICIAL STAMP</span></div>
            </div>
          </div>

          <div className="invoice-bottom-bar">
            <p>Thank you for your business! | Generated by Gismac Security Management System | www.secs.gismac.co.ke</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintInvoice;
